import Navbar from "./components/Navbar";
import FooterSection from "./components/FooterSection";
import ServiceVisual from "./components/ServiceVisual";

export default function NewFourContentEnginePage() {
  return (
    <div className="min-h-screen bg-[#fff8ef] text-[#171717] overflow-hidden">
      <div className="fixed inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(circle at 12% 18%, rgba(89,170,255,0.24), transparent 18%), radial-gradient(circle at 82% 14%, rgba(255,126,78,0.20), transparent 20%), radial-gradient(circle at 74% 68%, rgba(255,205,82,0.18), transparent 18%)" }} />
      <Navbar />
      <main className="relative z-10 pt-[110px] pb-20">
        <section className="pb-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#6b6b6b] font-bold mb-4">Service • Content Engine</p>
              <h1 className="text-5xl md:text-6xl font-black tracking-[-0.06em] leading-[0.94] mb-5">Publish less random content. Build a content machine that actually compounds.</h1>
              <p className="text-[#4f4f4f] text-lg leading-relaxed max-w-2xl mb-8">Flowvory turns content from a production treadmill into a growth asset by connecting topic structure, internal reinforcement, buying intent, and AI-assisted execution.</p>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => window.dispatchEvent(new Event("flowvory:open-strategy"))} className="px-7 py-4 rounded-full bg-[#171717] text-[#fff8ef] text-base font-bold shadow-[8px_8px_0_rgba(89,170,255,0.24)]">Book a Call</button>
                <div className="px-5 py-4 rounded-[22px] bg-white/80 border border-[#171717]/10 text-sm font-semibold">Built for brands that want content systems, not disconnected publishing.</div>
              </div>
            </div>
            <div className="rounded-[34px] border border-[#171717]/10 bg-white/82 p-7 shadow-[0_30px_100px_rgba(23,23,23,0.08)]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-4">What turns content from activity into infrastructure</p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["Topic map", "Cluster logic before production starts"],
                  ["Templates", "Reusable structures for speed and consistency"],
                  ["AI drafting", "Used inside guardrails, not as autopilot"],
                  ["Reinforcement", "Every page should strengthen the system"],
                ].map(([title, text], idx) => (
                  <div key={title} className={`rounded-[24px] border border-[#171717]/10 p-4 ${idx === 1 ? "bg-[#dff3ff] rotate-[1deg]" : idx === 2 ? "bg-[#fff0db] rotate-[-1deg]" : "bg-[#f9f7f1]"}`}>
                    <h3 className="text-lg font-black tracking-[-0.04em] mb-2">{title}</h3>
                    <p className="text-sm text-[#4f4f4f] leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 overflow-x-auto">
            <div className="min-w-[860px] rounded-[30px] border border-[#171717]/10 bg-white/86 shadow-[0_18px_50px_rgba(23,23,23,0.05)] overflow-hidden">
              <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] bg-[#171717] text-[#fff8ef] text-sm font-bold uppercase tracking-[0.16em]">
                <div className="px-5 py-4">Layer</div>
                <div className="px-5 py-4">Weak model</div>
                <div className="px-5 py-4">Flowvory model</div>
                <div className="px-5 py-4">Why it wins</div>
              </div>
              {[
                ["Planning", "Random briefs", "Cluster architecture", "Coverage compounds"],
                ["Writing", "Blank page every time", "Reusable systems", "Production speeds up"],
                ["Publishing", "Isolated pages", "Internal reinforcement", "Authority grows"],
              ].map((row, idx) => (
                <div key={row[0]} className={`grid grid-cols-[1.2fr_1fr_1fr_1fr] text-sm ${idx % 2 === 0 ? "bg-white/80" : "bg-[#eef7ff]"}`}>
                  {row.map((cell) => (
                    <div key={cell} className="px-5 py-4 border-t border-[#171717]/8 leading-relaxed">{cell}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-6 items-start">
            <ServiceVisual
              title="Content system maturity"
              subtitle="A content engine becomes more valuable when planning, reinforcement, and publishing quality improve together."
              bars={[
                { label: "Topic architecture", value: 88, color: "#59aaff" },
                { label: "Internal reinforcement", value: 79, color: "#66eec9" },
                { label: "Commercial alignment", value: 73, color: "#ff7e4e" },
              ]}
            />
            <div className="rounded-[30px] border border-[#171717]/10 bg-white/86 p-6 shadow-[0_18px_50px_rgba(23,23,23,0.05)] overflow-x-auto">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">How better planning changes what gets published</p>
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left border-b border-[#171717]/10">
                    <th className="pb-3 pr-4 font-bold">Step</th>
                    <th className="pb-3 pr-4 font-bold">Typical team</th>
                    <th className="pb-3 font-bold">Flowvory system</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Plan", "Topic ideas in a doc", "Cluster map with business logic"],
                    ["Produce", "One-off drafting", "Template-guided AI-assisted system"],
                    ["Publish", "Page goes live alone", "Page reinforces the rest of the site"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-[#171717]/8 align-top">
                      <td className="py-3 pr-4 font-semibold">{row[0]}</td>
                      <td className="py-3 pr-4 text-[#4f4f4f]">{row[1]}</td>
                      <td className="py-3 text-[#4f4f4f]">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-5">
            {[
              ["What breaks", "Most content programs create volume but not reinforcement, so the site gets bigger without getting stronger."],
              ["What improves", "A real engine ties together topic design, production logic, internal links, and commercial priorities."],
              ["Why this works", "We treat content as part of the operating model, not a separate department that publishes into the void."],
            ].map(([title, text], idx) => (
              <article key={title} className={`rounded-[30px] border border-[#171717]/10 p-6 shadow-[0_18px_50px_rgba(23,23,23,0.05)] ${idx === 1 ? "bg-[#171717] text-white rotate-[-1deg]" : idx === 2 ? "bg-[#e8fff7] rotate-[1deg]" : "bg-white/88"}`}>
                <h3 className="text-2xl font-black tracking-[-0.04em] leading-snug mb-3">{title}</h3>
                <p className={`leading-relaxed ${idx === 1 ? "text-white/76" : "text-[#4f4f4f]"}`}>{text}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
